import { useState, useCallback, useContext } from 'react';
import { AppContext } from '../context';
import { APICancelOrder, APICreateOrderDeletionRequest, parseApiError } from '../api';

/**
 * Единая логика отмены чека для всех страниц.
 *
 * Сервер отдаёт у каждого чека поле cancel_mode:
 *   'direct'  — можно отменить сразу (руководитель / finance.orders.delete);
 *   'request' — только через заявку (админ, рабочий аккаунт);
 *   'none'    — нельзя (нет прав либо чек уже отменён).
 *
 * onDone() — перезагрузка данных страницы после успеха.
 *
 * Использование:
 *   const { requestCancel, cancelBusyId, cancelModal } = useOrderCancel({ onDone: loadAll });
 *   <CheckCard onCancel={requestCancel} ... />
 *   <CancelOrderModal {...cancelModal} />
 */
export default function useOrderCancel({ onDone } = {}) {
    const { token, showToast } = useContext(AppContext);
    const [busyId, setBusyId] = useState(null);
    const [modal, setModal] = useState({
        open: false, order: null, total: null, reason: '', saving: false,
    });

    const closeModal = useCallback(
        () => setModal({ open: false, order: null, total: null, reason: '', saving: false }),
        [],
    );

    const openModal = useCallback(
        (order, total) => setModal({ open: true, order, total, reason: '', saving: false }),
        [],
    );

    // Прямая отмена — только тем, кому разрешил сервер
    const cancelDirect = useCallback(async (order, total) => {
        setBusyId(order.id);
        const res = await APICancelOrder(token, order.id);
        setBusyId(null);

        if (res?.error) {
            // Права могли измениться после загрузки списка — сервер вернёт
            // 403 с этим кодом. Молча переключаемся на сценарий заявки.
            if (res.error?.code === 'cancel_requires_request') {
                openModal(order, total);
                return;
            }
            showToast(parseApiError(res.error, 'Не удалось отменить чек'), 'danger');
            return;
        }
        showToast('Чек отменён');
        onDone && onDone();
    }, [token, showToast, onDone, openModal]);

    // Точка входа из карточки чека
    const requestCancel = useCallback((order, total) => {
        if (!order) return;

        if (order.pending_cancel_request_id) {
            showToast('По этому чеку уже отправлена заявка на отмену', 'warning');
            return;
        }

        // Старый сервер поля не отдаёт — считаем, что отмена прямая (прежнее поведение)
        const mode = order.cancel_mode ?? 'direct';
        if (mode === 'none') return;

        if (mode === 'request') {
            openModal(order, total);
            return;
        }

        if (window.confirm(
            'Отменить чек? Он останется в истории с пометкой «Отменён», ' +
            'но из выручки и расчётов исчезнет.'
        )) {
            cancelDirect(order, total);
        }
    }, [showToast, openModal, cancelDirect]);

    // Отправка заявки из модалки
    const submitRequest = useCallback(async () => {
        const reason = (modal.reason || '').trim();
        if (reason.length < 3) { showToast('Укажи причину отмены', 'warning'); return; }

        setModal(p => ({ ...p, saving: true }));
        const res = await APICreateOrderDeletionRequest(token, modal.order.id, reason, 'cancel');
        setModal(p => ({ ...p, saving: false }));

        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось отправить заявку'), 'danger');
            return;
        }
        closeModal();
        showToast('Заявка на отмену отправлена ✓');
        onDone && onDone();
    }, [token, modal, showToast, closeModal, onDone]);

    return {
        requestCancel,
        cancelBusyId: busyId,
        cancelModal: {
            open:   modal.open,
            order:  modal.order,
            total:  modal.total,
            reason: modal.reason,
            saving: modal.saving,
            onReasonChange: (v) => setModal(p => ({ ...p, reason: v })),
            onSubmit: submitRequest,
            onClose:  closeModal,
        },
    };
}